import angular from 'angular';
import { Account } from '@adjust/dashboard-core/app/components/account/account.service';
import { Agent } from '@adjust/dashboard-core/app/components/agent/agent.service';
import { Api } from '@adjust/dashboard-core/app/components/api/api.service';
import { configuration } from '@adjust/dashboard-core/app/components/configuration/configuration.service';
import { Helper } from '@adjust/dashboard-core/app/components/helper/helper.service';
import { Breadcrumbs } from '@adjust/dashboard-core/app/components/navbar/breadcrumbs/breadcrumbs.service';
import { Deployment } from '@adjust/dashboard-core/app/components/navbar/deployments/deployment.service';
import { Notification } from '@adjust/dashboard-core/app/components/navbar/notifications/notification.service';
import { ProductUpdate } from '@adjust/dashboard-core/app/components/product-updates/product-update.service';
import { Storage } from '@adjust/dashboard-core/app/components/storage/storage.service';
import { User } from '@adjust/dashboard-core/app/components/user/user.service';
import { Modal } from '@adjust/dashboard-core/app/components/modal/modal.service';
import {
  breadcrumbs,
  BreadcrumbsController,
} from '@adjust/dashboard-core/app/components/navbar/breadcrumbs/breadcrumbs.directive';
import {
  appSwitcher,
  AppSwitcherController,
} from '@adjust/dashboard-core/app/components/navbar/breadcrumbs/app-switcher/app-switcher.directive';
import {
  appDisplay,
  AppDisplayController,
} from '@adjust/dashboard-core/app/components/navbar/breadcrumbs/app-display/app-display.directive';
import { appDisplayContents } from '@adjust/dashboard-core/app/components/navbar/breadcrumbs/app-display/app-display-contents.directive';
import { deployments } from '@adjust/dashboard-core/app/components/navbar/deployments/deployments.directive';
import { navbar, NavbarController } from '@adjust/dashboard-core/app/components/navbar/navbar.directive';
import { notifications } from '@adjust/dashboard-core/app/components/navbar/notifications/notifications.directive';
import {
  notification,
  NotificationController,
} from '@adjust/dashboard-core/app/components/navbar/notifications/notification/notification.directive';
import { productNews } from '@adjust/dashboard-core/app/components/navbar/product-news/product-news.directive';
import {
  ProductUpdatesController,
  productUpdates,
} from '@adjust/dashboard-core/app/components/product-updates/product-updates.directive';
import { billingCurrencies } from '@adjust/dashboard-core/app/components/billing-currencies/billing-currencies.constant';
import { RightSidebar } from '@adjust/dashboard-core/app/components/right-sidebar/right-sidebar.service';
import {
  RightSidebarBreadcrumbsController,
  rightSidebarBreadcrumbs,
} from '@adjust/dashboard-core/app/components/right-sidebar-breadcrumbs/right-sidebar-breadcrumbs.directive';
import {
  Highlight,
  highlight,
} from '@adjust/dashboard-core/app/components/right-sidebar/highlight/highlight.directive';
import {
  rightSidebar,
  RightSidebarController,
} from '@adjust/dashboard-core/app/components/right-sidebar/right-sidebar.directive';
import {
  IncludeModalTplController,
  includeModalTpl,
} from '@adjust/dashboard-core/app/components/modal/include-modal-tpl/include-modal-tpl.directive';
import { ModalController, modal } from '@adjust/dashboard-core/app/components/modal/modal.directive';
import {
  TermsUpdateModalController,
  termsUpdateModal,
} from '@adjust/dashboard-core/app/components/terms-update-modal/terms-update-modal.directive';
import {
  EnvironmentBannerController,
  environmentBanner,
} from '@adjust/dashboard-core/app/components/environment-banner/environment-banner.directive';
import {
  WithSpacesController,
  withSpaces,
} from '@adjust/dashboard-core/app/components/with-spaces/with-spaces.directive';
import { range } from '@adjust/dashboard-core/app/components/filters/range/range.filter';

import { CustomSelectController } from './custom-select/custom-select.controller';
import { Transformer } from './api/transformer.service';
import { ServiceWorkerInterceptor } from './service-worker/service-worker-interceptor.service';
import { RedirectInterceptor } from './redirect-interceptor/redirect-interceptor';
import { AssetRequestInterceptor } from './asset-request-interceptor/asset-request-interceptor';
import { App } from './app/app.service';
import { Column } from './columns/column.service';
import { customSelectParse } from './custom-select/custom-select-parse.service';
import { Filter } from './filter/filter.service';
import { Grouping } from './grouping/grouping.service';
import { Loading } from './loading/loading.service';
import { Moment } from './moment/moment.service';
import { Scroll } from './scroll/scroll.service';
import { Table } from './table/table.service';
import { TimeSpan } from './time-span/time-span.service';
import { KpiCohorts } from './tracker/kpi-cohorts.service';
import { Kpi } from './tracker/kpi.service';
import { Tracker } from './tracker/tracker.service';
import { CheckboxTogglerController, checkboxToggler } from './checkbox-toggler/checkbox-toggler.directive';
import { CheckboxToggler } from './checkbox-toggler/checkbox-toggler';
import { ColumnsController, columns } from './columns/columns.directive';
import { ColumnSelectionController, columnSelection } from './column-selection/column-selection.directive';
import { customSelect } from './custom-select/custom-select.directive';
import { datepickerWrapperInline } from './datepicker-wrapper/datepicker-wrapper-inline.directive';
import { datepickerWrapper } from './datepicker-wrapper/datepicker-wrapper.directive';
import { ErrorController, error } from './error/error.directive';
import { filterAttributionSource } from './filter/filter-attribution-source/filter-attribution-source.directive';
import { filterConversionType } from './filter/filter-conversion-type/filter-conversion-type.directive';
import { filterRegions } from './filter/filter-regions/filter-regions.directive';
import { filterCountries } from './filter/filter-countries/filter-countries.directive';
import { filterGrouping } from './filter/filter-grouping/filter-grouping.directive';
import { filterImpression } from './filter/filter-impression/filter-impression.directive';
import { filterKpi } from './filter/filter-kpi/filter-kpi.directive';
import { filterPartners } from './filter/filter-partners/filter-partners.directive';
import { filterOsNames } from './filter/filter-os-names/filter-os-names.directive';
import { filterPlatforms } from './filter/filter-platforms/filter-platforms.directive';
import { filterSandbox } from './filter/filter-sandbox/filter-sandbox.directive';
import { filterTimezone } from './filter/filter-timezone/filter-timezone.directive';
import { FilterController, filter } from './filter/filter.directive';
import { FilterExclusionController, filterExclusion } from './filter/filter-exclusion/filter-exclusion.directive';
import {
  FlyoutContentController,
  flyoutContent,
  FlyoutBtnController,
  flyoutBtn,
  FlyoutController,
  flyout,
} from './flyout/flyout.directive';
import { FocusMeController, focusMe } from './focus-me/focus-me.directive';
import { FocusController, focus } from './focus/focus.directive';
import { hideOnBeta } from './hide-on-beta/hide-on-beta.directive';
import { isValid } from './is-valid/is-valid.directive';
import { LoadingController, loadingSpinner, loading } from './loading/loading.directive';
import { PageTitle } from './page-title/page-title.service';
import { required } from './required/required.directive';
import { RoundNumberController, roundNumber } from './round-number/round-number.directive';
import { ScrollBarController, scrollBar } from './scroll/scroll-bar.directive';
import { ScrollSideController, scrollSide } from './scroll/scroll-side.directive';
import { ScrollTableController, scrollTable } from './scroll/scroll-table.directive';
import { ScrollThumbController, scrollThumb } from './scroll/scroll-thumb.directive';
import { ScrollTriggerController, scrollTrigger } from './scroll/scroll-trigger.directive';
import { ScrollController, scroll } from './scroll/scroll.directive';
import { SearchController, search } from './search/search.directive';
import { SelectifyController, selectify } from './selectify/selectify.directive';
import { sideHeader } from './side-header/side-header.directive';
import { StatusController, status } from './status/status.directive';
import { switcher } from './switcher/switcher.directive';
import { TimeSpanController, timeSpan } from './time-span/time-span.directive';
import { fromNow } from './from-now/from-now.filter';
import { humanize } from './humanize/humanize.filter';
import { momentFormat } from './moment-format/moment-format.filter';
import { percent } from './percent/percent.filter';
import { rank } from './rank/rank.filter';
import { roundNumber as roundNumberFilter } from './round-number/round-number.filter';
import { round } from './round/round.filter';
import { shortCurrency } from './short-currency/short-currency.filter';
import { shortNumber } from './short-number/short-number.filter';
import { timespan } from './timespan/timespan.filter';
import { regions } from './regions/regions.constant';
import { countries } from './countries/countries.constant';
import { filterConstants } from './filter/filter.constant';
import { options } from './filter/options.constant';
import { platforms } from './filter/platforms.constant';
import { shortKpis } from './tracker/short-kpis.constant';
import { dndDraggable, DraggableController } from './drag-and-drop/draggable.directive';
import { Sortable } from './drag-and-drop/sortable.service';
import { draggableClasses } from './drag-and-drop/draggable.constant';
import { UserDetailsFieldsController, userDetailsFields } from './user-details-fields/user-details-fields.directive';
import { CustomPeriod } from './custom-period/custom-period.service';
import { ReduxActions } from './redux-actions/redux-actions.service';

export default angular
  .module('adjust.components', [])

  .controller('CustomSelectController', CustomSelectController)
  .factory('Account', Account)
  .factory('Agent', Agent)
  .factory('Api', Api)
  .factory('Transformer', Transformer)
  .factory('AssetRequestInterceptor', AssetRequestInterceptor)
  .factory('ServiceWorkerInterceptor', ServiceWorkerInterceptor)
  .factory('RedirectInterceptor', RedirectInterceptor)
  .factory('App', App)
  .factory('Column', Column)
  .service('configuration', configuration)
  .factory('customSelectParse', customSelectParse)
  .factory('Filter', Filter)
  .factory('Grouping', Grouping)
  .factory('Helper', Helper)
  .factory('Loading', Loading)
  .service('Modal', Modal)
  .factory('Moment', Moment)
  .factory('Breadcrumbs', Breadcrumbs)
  .factory('Deployment', Deployment)
  .factory('Notification', Notification)
  .factory('ProductUpdate', ProductUpdate)
  .factory('RightSidebar', RightSidebar)
  .factory('Scroll', Scroll)
  .factory('Storage', Storage)
  .factory('Table', Table)
  .factory('TimeSpan', TimeSpan)
  .factory('KpiCohorts', KpiCohorts)
  .factory('Kpi', Kpi)
  .factory('Tracker', Tracker)
  .factory('User', User)
  .factory('CheckboxToggler', CheckboxToggler)
  .controller('CheckboxTogglerController', CheckboxTogglerController)
  .directive('checkboxToggler', checkboxToggler)
  .controller('ColumnsController', ColumnsController)
  .directive('columns', columns)
  .controller('ColumnSelectionController', ColumnSelectionController)
  .directive('columnSelection', columnSelection)
  .directive('customSelect', customSelect)
  .directive('datepickerWrapperInline', datepickerWrapperInline)
  .directive('datepickerWrapper', datepickerWrapper)
  .controller('EnvironmentBannerController', EnvironmentBannerController)
  .directive('environmentBanner', environmentBanner)
  .controller('ErrorController', ErrorController)
  .directive('error', error)
  .directive('filterAttributionSource', filterAttributionSource)
  .directive('filterConversionType', filterConversionType)
  .directive('filterRegions', filterRegions)
  .directive('filterCountries', filterCountries)
  .directive('filterGrouping', filterGrouping)
  .directive('filterImpression', filterImpression)
  .directive('filterKpi', filterKpi)
  .directive('filterPartners', filterPartners)
  .directive('filterOsNames', filterOsNames)
  .directive('filterPlatforms', filterPlatforms)
  .directive('filterSandbox', filterSandbox)
  .directive('filterTimezone', filterTimezone)
  .controller('FilterController', FilterController)
  .directive('filter', filter)
  .controller('FilterExclusionController', FilterExclusionController)
  .directive('filterExclusion', filterExclusion)
  .controller('FlyoutContentController', FlyoutContentController)
  .directive('flyoutContent', flyoutContent)
  .controller('FlyoutBtnController', FlyoutBtnController)
  .directive('flyoutBtn', flyoutBtn)
  .controller('FlyoutController', FlyoutController)
  .directive('flyout', flyout)
  .controller('FocusMeController', FocusMeController)
  .directive('focusMe', focusMe)
  .controller('FocusController', FocusController)
  .directive('focus', focus)
  .directive('hideOnBeta', hideOnBeta)
  .directive('isValid', isValid)
  .controller('LoadingController', LoadingController)
  .directive('loadingSpinner', loadingSpinner)
  .directive('loading', loading)
  .controller('IncludeModalTplController', IncludeModalTplController)
  .directive('includeModalTpl', includeModalTpl)
  .controller('ModalController', ModalController)
  .directive('modal', modal)
  .directive('breadcrumbs', breadcrumbs)
  .controller('BreadcrumbsController', BreadcrumbsController)
  .directive('appSwitcher', appSwitcher)
  .controller('AppSwitcherController', AppSwitcherController)
  .directive('appDisplay', appDisplay)
  .controller('AppDisplayController', AppDisplayController)
  .directive('appDisplayContents', appDisplayContents)
  .directive('deployments', deployments)
  .directive('navbar', navbar)
  .controller('NavbarController', NavbarController)
  .directive('notifications', notifications)
  .directive('notification', notification)
  .controller('NotificationController', NotificationController)
  .directive('productNews', productNews)
  .service('PageTitle', PageTitle)
  .controller('ProductUpdatesController', ProductUpdatesController)
  .directive('productUpdates', productUpdates)
  .directive('required', required)
  .controller('RightSidebarBreadcrumbsController', RightSidebarBreadcrumbsController)
  .directive('rightSidebarBreadcrumbs', rightSidebarBreadcrumbs)
  .factory('Highlight', Highlight)
  .directive('highlight', highlight)
  .directive('rightSidebar', rightSidebar)
  .controller('RightSidebarController', RightSidebarController)
  .controller('RoundNumberController', RoundNumberController)
  .directive('roundNumber', roundNumber)
  .controller('ScrollBarController', ScrollBarController)
  .directive('scrollBar', scrollBar)
  .controller('ScrollSideController', ScrollSideController)
  .directive('scrollSide', scrollSide)
  .controller('ScrollTableController', ScrollTableController)
  .directive('scrollTable', scrollTable)
  .controller('ScrollThumbController', ScrollThumbController)
  .directive('scrollThumb', scrollThumb)
  .controller('ScrollTriggerController', ScrollTriggerController)
  .directive('scrollTrigger', scrollTrigger)
  .controller('ScrollController', ScrollController)
  .directive('scroll', scroll)
  .controller('SearchController', SearchController)
  .directive('search', search)
  .controller('SelectifyController', SelectifyController)
  .directive('selectify', selectify)
  .directive('sideHeader', sideHeader)
  .controller('StatusController', StatusController)
  .directive('status', status)
  .directive('switcher', switcher)
  .controller('TimeSpanController', TimeSpanController)
  .directive('timeSpan', timeSpan)
  .filter('fromNow', fromNow)
  .filter('humanize', humanize)
  .filter('momentFormat', momentFormat)
  .filter('percent', percent)
  .filter('range', range)
  .filter('rank', rank)
  .filter('roundNumber', roundNumberFilter)
  .filter('round', round)
  .filter('shortCurrency', shortCurrency)
  .filter('shortNumber', shortNumber)
  .filter('timespan', timespan)
  .constant('billingCurrencies', billingCurrencies)
  .constant('regions', regions)
  .constant('countries', countries)
  .constant('filterConstants', filterConstants)
  .constant('options', options)
  .constant('platforms', platforms)
  .constant('shortKpis', shortKpis)
  .constant('conf', configuration())
  .factory('Sortable', Sortable)
  .controller('DraggableController', DraggableController)
  .directive('dndDraggable', dndDraggable)
  .constant('draggableClasses', draggableClasses)
  .controller('UserDetailsFieldsController', UserDetailsFieldsController)
  .directive('userDetailsFields', userDetailsFields)
  .controller('TermsUpdateModalController', TermsUpdateModalController)
  .directive('termsUpdateModal', termsUpdateModal)
  .controller('WithSpacesController', WithSpacesController)
  .directive('withSpaces', withSpaces)
  .factory('ReduxActions', ReduxActions)
  .factory('CustomPeriod', CustomPeriod).name;
